// "More roots": every kit root and अस in a searchable list, grouped अकारान्त · आ / ए / ओ · अस.
// Search by Devanagari, Hindi, English or plain Latin (padh → पढ). Arrow keys move through the list, Enter chooses.

import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { LabRoot } from '@/prakrit/components/grammar/labs/verb-data'

import { useId, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DhatuPicture } from '@/prakrit/components/common/dhatu-picture'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { hiDigits, listKeyNav } from '@/prakrit/components/grammar/labs/lab-ui'
import { matches, normaliseQuery } from '@/prakrit/components/grammar/labs/lab-search'
import { ROOT_GROUPS } from '@/prakrit/components/grammar/labs/verb-data'

interface RootComboboxProps {
    roots: LabRoot[]
    value: string
    onChange: (root: string) => void
    /** Show the chosen root on the button; false shows a "Choose…" prompt (the root is already a tile). */
    showValue: boolean
    className?: string
}

export const RootCombobox: FC<RootComboboxProps> = ({ roots, value, onChange, showValue, className }): ReactNode => {
    const searchId = useId()
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const selected = roots.find(r => r.root === value)
    const q = useMemo(() => normaliseQuery(query), [query])

    const groups = useMemo(() => ROOT_GROUPS
        .map(g => ({ ...g, items: roots.filter(r => r.group === g.value && matches(r.search, q)) }))
        .filter(g => g.items.length > 0), [roots, q])

    const total = groups.reduce((sum, g) => sum + g.items.length, 0)

    const choose = (root: string) => {
        onChange(root)
        setOpen(false)
        setQuery('')
    }

    const onSearchKey = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            listRef.current?.querySelector<HTMLElement>('[data-nav-item]')?.focus()
        }
        else if (event.key === 'Enter' && groups[0]?.items[0]) {
            event.preventDefault()
            choose(groups[0].items[0].root)
        }
    }

    const current = showValue && selected ? selected : null

    return (
        <Popover
            open={open}
            onOpenChange={next => {
                setOpen(next)

                if (!next) setQuery('')
            }}
        >
            <PopoverTrigger
                aria-label={current ? `धातु: ${current.root} (${current.en}). सभी धातुएँ · All roots` : 'और धातुएँ चुनें · Choose from all roots'}
                className={cn(
                    'flex min-h-tap w-full items-center justify-between gap-3 rounded-2xl border bg-card px-4 py-1.5 text-left transition-colors',
                    'hover:border-saffron/60 data-popup-open:border-saffron',
                    current ? 'border-foreground/40 ring-1 ring-foreground/15' : 'border-border',
                    className
                )}
            >
                {current ? (
                    <span className='flex min-w-0 items-baseline gap-2'>
                        <span lang='pra' className='font-prakrit text-lg font-bold text-prakrit'>{current.root}</span>
                        <span className='truncate text-sm text-muted-foreground'>
                            <span lang='hi' className='hindi'>{current.hi}</span>
                            <span aria-hidden='true'> · </span>
                            <span lang='en'>{current.en}</span>
                        </span>
                    </span>
                ) : (
                    <span className='text-sm text-muted-foreground'>
                        <span lang='hi' className='hindi'>चुनें…</span>
                        <span aria-hidden='true'> · </span>
                        <span lang='en'>Choose…</span>
                    </span>
                )}
                <ChevronDown className='size-4 shrink-0 text-muted-foreground' aria-hidden='true' />
            </PopoverTrigger>

            <PopoverContent align='start' initialFocus={inputRef} className='w-[min(22rem,calc(100vw-2rem))] gap-2 rounded-2xl p-2'>
                <div className='relative'>
                    <label htmlFor={searchId} className='sr-only'>धातु खोजें · Search roots</label>
                    <Search className='pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                    <input
                        ref={inputRef}
                        id={searchId}
                        type='search'
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        onKeyDown={onSearchKey}
                        placeholder='पढ, देखना, to see, padh…'
                        autoComplete='off'
                        autoCorrect='off'
                        spellCheck={false}
                        className='min-h-tap w-full rounded-xl border border-input bg-background py-2 pl-9 pr-3 text-base outline-none placeholder:text-muted-foreground/80 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/40'
                    />
                </div>
                <p aria-live='polite' className='px-1 text-xs text-muted-foreground'>
                    <span lang='hi' className='hindi'>{hiDigits(total)} धातुएँ</span>
                    <span aria-hidden='true'> · </span>
                    <span lang='en'>{total} {total === 1 ? 'root' : 'roots'}</span>
                </p>
                <div ref={listRef} onKeyDown={listKeyNav} className='-mx-1 max-h-72 overflow-y-auto overscroll-contain px-1'>
                    {total === 0 ? (
                        <p className='rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground'>
                            <span lang='hi' className='hindi'>इस खोज से कोई धातु नहीं मिली।</span>
                            <span aria-hidden='true'> · </span>
                            <span lang='en'>No root matches this search.</span>
                        </p>
                    ) : groups.map(g => (
                        <div key={g.value} role='group' aria-label={`${g.hi} · ${g.en}`} className='pb-1'>
                            <p aria-hidden='true' className='px-2 pb-1 pt-2 text-xs font-semibold text-muted-foreground'>
                                <span lang='hi' className='hindi'>{g.hi}</span>
                                <span> · </span>
                                <span lang='en' className='font-medium'>{g.en}</span>
                            </p>
                            <ul className='flex flex-col gap-0.5'>
                                {g.items.map(r => {
                                    const active = r.root === value

                                    return (
                                        <li key={r.root}>
                                            <button
                                                type='button'
                                                data-nav-item=''
                                                aria-pressed={active}
                                                onClick={() => choose(r.root)}
                                                className={cn(
                                                    'flex min-h-tap w-full items-center gap-3 rounded-xl px-2.5 py-1 text-left transition-colors hover:bg-secondary focus-visible:bg-secondary',
                                                    active && 'bg-saffron/12'
                                                )}
                                            >
                                                <DhatuPicture root={r.root} decorative sizes='40px' className='size-9 shrink-0 rounded-lg' fallback={<span aria-hidden='true' className='size-9 shrink-0 rounded-lg bg-secondary' />} />
                                                <span lang='pra' className='min-w-[3.25rem] font-prakrit text-lg font-bold text-prakrit'>{r.root}</span>
                                                <span className='flex min-w-0 flex-1 flex-col leading-tight'>
                                                    <span lang='hi' className='hindi truncate text-sm'>{r.hi}</span>
                                                    <span lang='en' className='truncate text-xs text-muted-foreground'>{r.en}</span>
                                                </span>
                                                {active && <Check className='size-4 shrink-0 text-saffron-strong' aria-hidden='true' />}
                                            </button>
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    )
}
